import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Download, Github, Linkedin, Mail, ChevronRight } from "lucide-react";
import profilepic from "../../assets/myprofilepic_enhanced-Photoroom.png";
import backgroundImage from "../../assets/heroimage.jpg";

const fullText =
  "A dedicated full-stack engineer with a passion for building dynamic, high-performance systems. From database architecture to frontend experiences, I craft scalable solutions that solve real-world problems efficiently and reliably.";

export default function HeroSection() {
  const [displayText, setDisplayText] = useState("");
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (index < fullText.length) {
      const timer = setTimeout(() => {
        setDisplayText((prev) => prev + fullText[index]);
        setIndex((prev) => prev + 1);
      }, 30);
      return () => clearTimeout(timer);
    }
  }, [index]);

  return (
    <>
      <section className="relative min-h-screen w-full overflow-hidden pt-5 md:pt-5">
        {/* Background Image */}
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundAttachment: "fixed",
            backgroundRepeat: "no-repeat",
          }}
        >
          <div className="absolute inset-0 bg-black/85"></div>
        </div>

        {/* Animated Background Elements */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-white/5 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-screen flex items-center py-12">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center w-full">
            {/* Left Side - Text Content */}
            <div className="space-y-4 md:space-y-6 text-center lg:text-left">
              {/* Badge */}
              <div className="flex justify-center lg:justify-start mb-2">
                <div className="inline-flex items-center gap-2 bg-blue-50/10 backdrop-blur-sm border border-blue-200/30 px-3 py-1 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400"></span>
                  <span className="text-blue-300 text-xs font-semibold tracking-wide">
                    Fullstack Development
                  </span>
                </div>
              </div>

              <div className="space-y-1 md:space-y-2">
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold">
                  {/* Reduced from lg:text-7xl to lg:text-6xl */}
                  <span className="text-white">Jedidah</span>
                  <span className="text-blue-400"> Nzila</span>
                </h1>
                <p className="text-xl sm:text-2xl md:text-3xl text-gray-400 mt-1 md:mt-2">
                  Full Stack Developer
                </p>
              </div>

              <p className="text-gray-300 text-base sm:text-lg leading-relaxed max-w-lg mx-auto lg:mx-0">
                {displayText}
                <span className="inline-block w-0.5 h-4 sm:h-5 ml-1 bg-blue-400 animate-pulse"></span>
              </p>

              <div className="flex flex-wrap gap-3 sm:gap-4 pt-2 sm:pt-4 justify-center lg:justify-start">
                <Link to="/contact">
                  <button className="bg-blue-500 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-full font-semibold hover:bg-blue-600 transition-all hover:scale-105 flex items-center gap-2 group text-sm sm:text-base">
                    Collaborate{" "}
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </Link>
                <a
                  href="/Resume.docx"
                  download="JedidahNzila.docx"
                  className="border-2 border-blue-500 text-blue-500 px-6 sm:px-8 py-2 sm:py-3 rounded-full font-semibold hover:bg-blue-500 hover:text-white transition-all hover:scale-105 flex items-center gap-2 text-sm sm:text-base"
                >
                  Download CV <Download size={18} />
                </a>
              </div>

              {/* Stats Section */}
              <div className="flex gap-6 sm:gap-8 pt-4 sm:pt-8 justify-center lg:justify-start">
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-white">
                    1+
                  </div>
                  <div className="text-gray-400 text-xs sm:text-sm">
                    Years Experience
                  </div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-white">
                    5+
                  </div>
                  <div className="text-gray-400 text-xs sm:text-sm">
                    Projects Done
                  </div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-white">
                    5+
                  </div>
                  <div className="text-gray-400 text-xs sm:text-sm">Skills</div>
                </div>
              </div>

              {/* Social Links */}
              <div className="flex gap-3 sm:gap-4 pt-2 sm:pt-4 justify-center lg:justify-start">
                {[
                  {
                    Icon: Github,
                    href: "https://github.com/jedidahnzila",
                    label: "GitHub",
                  },
                  {
                    Icon: Linkedin,
                    href: "https://www.linkedin.com/in/jedidah-nzila-a921a6321/",
                    label: "LinkedIn",
                  },
                  {
                    Icon: Mail,
                    href: "mailto:nzilajedidah@gmail.com",
                    label: "Email",
                  },
                ].map(({ Icon, href, label }, i) => (
                  <a
                    key={i}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-gray-900/80 backdrop-blur-sm text-gray-400 hover:bg-blue-500 hover:text-white transition-all hover:scale-110"
                    aria-label={label}
                  >
                    <Icon size={16} className="sm:w-[18px] sm:h-[18px]" />
                  </a>
                ))}
              </div>
            </div>

            {/* Right Side - Profile Image */}
            <div className="flex justify-center lg:justify-end mt-8 lg:mt-0">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full blur-2xl opacity-20 animate-pulse"></div>
                <div className="relative w-56 h-56 sm:w-64 sm:h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 rounded-full overflow-hidden border-4 border-blue-500/30 shadow-2xl backdrop-blur-sm">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent z-10"></div>
                  <img
                    src={profilepic}
                    alt="Jedidah Nzila"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-2 -right-2 sm:-bottom-3 sm:-right-3 bg-black/90 backdrop-blur-sm rounded-full px-3 py-1 sm:px-4 sm:py-2 border border-blue-500 shadow-lg">
                  <div className="flex items-center gap-1 sm:gap-2">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-white text-xs sm:text-sm font-medium">
                      Available
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-gray-700 rounded-full flex justify-center bg-black/20 backdrop-blur-sm">
            <div className="w-1 h-3 bg-blue-400 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>
    </>
  );
}
